import * as yup from 'yup';
import { ERROR_CODES } from './errorCopy';
import { stateNames, states } from '../../utils/form';
const REGEX = {
    WHOLE_STRING_MUST_MATCH: /^[a-zA-Z0-9 `~.\/\-']*$/,
    VALID_ZIP: /^[0-9]{5}$/,
    VALID_ZIP_NON_US: /^[A-Za-z0-9](?:[A-Za-z0-9 ]{0,5}[A-Za-z0-9])?$/,
    VALID_STATE: /^[A-Za-z0-9 ]*$/,
    CONTAINS_MIN_LETTERS: /[a-zA-Z]/g,
    CONTAINS_MIN_CHARS_NON_US: /[a-zA-Z0-9]/g,
    VALID_ROUTING_NUMBER: /^[0-9]{9}$/,
};
const parseDate = (value) => {
    const sanitizedValue = value.replace('/', '');
    const month = parseInt(sanitizedValue.slice(0, 2), 10);
    const year = parseInt(sanitizedValue.slice(2), 10);
    return [month, year];
};
export const paymentFormSchema = yup.object().shape({
    name: yup
        .string()
        .trim()
        .required(ERROR_CODES.REQUIRED)
        .matches(REGEX.WHOLE_STRING_MUST_MATCH, ERROR_CODES.CREDITCARD.NAME_ON_CARD.INVALID_CHARACTERS)
        .test('minTwoLetters', ERROR_CODES.CREDITCARD.NAME_ON_CARD.MIN_LETTERS_REQ, (value = '') => {
        const letterCount = (value.match(REGEX.CONTAINS_MIN_LETTERS) || []).length;
        return letterCount >= 2;
    }),
    expirationDate: yup
        .string()
        .trim()
        .required(ERROR_CODES.REQUIRED)
        .test('isValidDate', ERROR_CODES.CREDITCARD.EXPIRATION.INVALID_FORMAT, (value = '') => {
        const [month, year] = parseDate(value);
        if (month === undefined ||
            year === undefined ||
            isNaN(month) ||
            isNaN(year) ||
            month < 1 ||
            month > 12) {
            return false;
        }
        const date = new Date(year, month - 1);
        return date.getFullYear() === year && date.getMonth() === month - 1;
    })
        .test('isExpiredDate', ERROR_CODES.CREDITCARD.EXPIRATION.FUTURE_DATE_REQ, (value = '') => {
        const [month, year] = parseDate(value);
        if (month === undefined || year === undefined) {
            return false;
        }
        const expirationDate = new Date(year, month);
        return expirationDate > new Date();
    }),
    cvv: yup
        .string()
        .trim()
        .required(ERROR_CODES.REQUIRED)
        .test('isValidAmex', ERROR_CODES.CREDITCARD.CVV.INVALID_AMEX_CVV, function () {
        const { context = {} } = this.options;
        const cvv = this.parent ? this.parent.cvv : '';
        if (context.cardtype && context.cardtype === 'AMEX') {
            return cvv.length === 4;
        }
        return true;
    })
        .test('isValidNonAmex', ERROR_CODES.CREDITCARD.CVV.INVALID_NON_AMEX_CVV, function () {
        const { context = {} } = this.options;
        const cvv = this.parent ? this.parent.cvv : '';
        if (context.cardtype && context.cardtype !== 'AMEX') {
            return cvv.length === 3;
        }
        return true;
    })
        .min(3, ERROR_CODES.CREDITCARD.CVV.INVALID_NON_AMEX_CVV)
        .max(4, ERROR_CODES.CREDITCARD.CVV.INVALID_AMEX_CVV),
    country: yup.string().trim().required(ERROR_CODES.REQUIRED),
    streetAddress: yup
        .string()
        .trim()
        .required(ERROR_CODES.REQUIRED)
        .matches(REGEX.WHOLE_STRING_MUST_MATCH, ERROR_CODES.ADDRESS.STREET_ADDRESS.INVALID_CHARACTERS)
        .test('minTwoLetters', ERROR_CODES.CREDITCARD.NAME_ON_CARD.MIN_LETTERS_REQ, (value = '') => {
        const letterCount = (value.match(REGEX.CONTAINS_MIN_LETTERS) || []).length;
        return letterCount >= 2;
    }),
    streetAddress2: yup
        .string()
        .trim()
        .matches(REGEX.WHOLE_STRING_MUST_MATCH, ERROR_CODES.ADDRESS.STREET_ADDRESS_2.INVALID_CHARACTERS),
    city: yup
        .string()
        .trim()
        .required(ERROR_CODES.REQUIRED)
        .matches(REGEX.WHOLE_STRING_MUST_MATCH, ERROR_CODES.ADDRESS.CITY.INVALID_CHARACTERS)
        .test('validCityminlength', '', function () {
        const { context = {} } = this.options;
        const parent = this.parent ? this.parent : {};
        const country = context.country || parent.country || '';
        const city = parent.city || '';
        const letterCount = (city.match(REGEX.CONTAINS_MIN_LETTERS) || []).length;
        if (country === 'United States of America' || country === '') {
            if (letterCount < 3) {
                return this.createError({ message: 'Address_City_MinRequired' });
            }
        }
        else {
            if (letterCount < 2) {
                return this.createError({ message: 'Address_StreetAddress_MinRequired' });
            }
        }
        return true;
    }),
    state: yup
        .string()
        .trim()
        .required(ERROR_CODES.REQUIRED)
        .matches(REGEX.VALID_STATE, ERROR_CODES.ADDRESS.STATE.INVALID_CHARACTERS)
        .test('validState', '', function () {
        const { context = {} } = this.options;
        const parent = this.parent ? this.parent : {};
        const country = context.country || parent.country || '';
        const state = parent.state || '';
        if (country === 'United States of America' || country === '') {
            // For US, validate against the state dropdown list (case-insensitive)
            // Accept both full state names (e.g., "Texas") and abbreviations (e.g., "TX")
            const stateNamesLower = stateNames.map((s) => s.toLowerCase());
            const stateCodesLower = states.map((s) => s.value.toLowerCase());
            const stateLower = state.toLowerCase();
            const isValidStateName = stateNamesLower.includes(stateLower);
            const isValidStateCode = stateCodesLower.includes(stateLower);
            if (!isValidStateName && !isValidStateCode) {
                return this.createError({ message: ERROR_CODES.ADDRESS.STATE.INVALID_SELECTION });
            }
        }
        else {
            // For non-US, just check minimum letters
            const letterCount = (state.match(REGEX.CONTAINS_MIN_LETTERS) || []).length;
            if (letterCount < 2) {
                return this.createError({ message: 'Address_StreetAddress_MinRequired' });
            }
        }
        return true;
    }),
    zip: yup
        .string()
        .trim()
        .required(ERROR_CODES.REQUIRED)
        .matches(REGEX.VALID_ZIP, ERROR_CODES.ADDRESS.ZIP_FIVE.FIVE_DIGITS_REQUIRED),
    postalCode: yup
        .string()
        .trim()
        .required(ERROR_CODES.REQUIRED)
        .test('validPostalCode', '', function () {
        const { context = {} } = this.options;
        const parent = this.parent ? this.parent : {};
        const country = context.country || parent.country || '';
        const postalCode = parent.postalCode || '';
        if (country === 'United States of America') {
            if (!REGEX.VALID_ZIP.test(postalCode)) {
                return this.createError({ message: 'Address_Zip_FiveDigitsRequired' });
            }
        }
        else {
            if (!REGEX.VALID_ZIP_NON_US.test(postalCode)) {
                return this.createError({ message: 'PostalCodeNonUS_InvalidCharacters' });
            }
            // Check for minimum 4 alphanumeric characters (ignoring spaces)
            const letterCount = (postalCode.match(REGEX.CONTAINS_MIN_CHARS_NON_US) || []).length;
            if (letterCount < 4) {
                return this.createError({ message: 'PostalCodeNonUS_MinRequired' });
            }
        }
        return true;
    }),
});
export const paymentACHFormSchema = yup.object().shape({
    accountType: yup.string().trim().required(ERROR_CODES.RADIO_BUTTON_REQUIRED),
    accountName: yup
        .string()
        .trim()
        .required(ERROR_CODES.REQUIRED)
        .matches(REGEX.WHOLE_STRING_MUST_MATCH, ERROR_CODES.CREDITCARD.NAME_ON_CARD.INVALID_CHARACTERS)
        .test('minTwoLetters', ERROR_CODES.CREDITCARD.NAME_ON_CARD.MIN_LETTERS_REQ, (value = '') => {
        const letterCount = (value.match(REGEX.CONTAINS_MIN_LETTERS) || []).length;
        return letterCount >= 2;
    }),
    bankName: yup
        .string()
        .trim()
        .required(ERROR_CODES.REQUIRED)
        .matches(REGEX.WHOLE_STRING_MUST_MATCH, ERROR_CODES.CREDITCARD.NAME_ON_CARD.INVALID_CHARACTERS)
        .test('minTwoLetters', ERROR_CODES.CREDITCARD.NAME_ON_CARD.MIN_LETTERS_REQ, (value = '') => {
        const letterCount = (value.match(REGEX.CONTAINS_MIN_LETTERS) || []).length;
        return letterCount >= 2;
    }),
    routingNumber: yup
        .string()
        .trim()
        .required(ERROR_CODES.REQUIRED)
        .matches(REGEX.VALID_ROUTING_NUMBER, ERROR_CODES.ACH.ROUTING_NUMBER.INVALID),
    accountDescription: yup
        .string()
        .trim()
        .required(ERROR_CODES.REQUIRED)
        .matches(REGEX.WHOLE_STRING_MUST_MATCH, ERROR_CODES.ACH.ACCOUNT_DESCRIPTION.INVALID_CHARACTERS)
        .test('minTwoLetters', ERROR_CODES.CREDITCARD.NAME_ON_CARD.MIN_LETTERS_REQ, (value = '') => {
        const letterCount = (value.match(REGEX.CONTAINS_MIN_LETTERS) || []).length;
        return letterCount >= 2;
    }),
    streetAddress: yup
        .string()
        .trim()
        .required(ERROR_CODES.REQUIRED)
        .matches(REGEX.WHOLE_STRING_MUST_MATCH, ERROR_CODES.ADDRESS.STREET_ADDRESS.INVALID_CHARACTERS)
        .test('minTwoLetters', ERROR_CODES.CREDITCARD.NAME_ON_CARD.MIN_LETTERS_REQ, (value = '') => {
        const letterCount = (value.match(REGEX.CONTAINS_MIN_LETTERS) || []).length;
        return letterCount >= 2;
    }),
    streetAddress2: yup
        .string()
        .trim()
        .matches(REGEX.WHOLE_STRING_MUST_MATCH, ERROR_CODES.ADDRESS.STREET_ADDRESS_2.INVALID_CHARACTERS),
    city: yup
        .string()
        .trim()
        .required(ERROR_CODES.REQUIRED)
        .matches(REGEX.WHOLE_STRING_MUST_MATCH, ERROR_CODES.ADDRESS.CITY.INVALID_CHARACTERS)
        .test('validCityminlength', '', function () {
        const { context = {} } = this.options;
        const parent = this.parent ? this.parent : {};
        const country = context.country || parent.country || '';
        const city = parent.city || '';
        const letterCount = (city.match(REGEX.CONTAINS_MIN_LETTERS) || []).length;
        if (country === 'United States of America' || country === '') {
            if (letterCount < 3) {
                return this.createError({ message: 'Address_City_MinRequired' });
            }
        }
        else {
            if (letterCount < 2) {
                return this.createError({ message: 'Address_StreetAddress_MinRequired' });
            }
        }
        return true;
    }),
    state: yup
        .string()
        .trim()
        .required(ERROR_CODES.REQUIRED)
        .matches(REGEX.VALID_STATE, ERROR_CODES.ADDRESS.STATE.INVALID_CHARACTERS)
        .test('validState', '', function () {
        const parent = this.parent ? this.parent : {};
        const state = parent.state || '';
        // ACH forms are US-only, so always validate against state list (case-insensitive)
        // Accept both full state names (e.g., "Texas") and abbreviations (e.g., "TX")
        const stateNamesLower = stateNames.map((s) => s.toLowerCase());
        const stateCodesLower = states.map((s) => s.value.toLowerCase());
        const stateLower = state.toLowerCase();
        const isValidStateName = stateNamesLower.includes(stateLower);
        const isValidStateCode = stateCodesLower.includes(stateLower);
        if (!isValidStateName && !isValidStateCode) {
            return this.createError({ message: ERROR_CODES.ADDRESS.STATE.INVALID_SELECTION });
        }
        return true;
    }),
    zip: yup
        .string()
        .trim()
        .required(ERROR_CODES.REQUIRED)
        .matches(REGEX.VALID_ZIP, ERROR_CODES.ADDRESS.ZIP_FIVE.FIVE_DIGITS_REQUIRED),
});
//# sourceMappingURL=paymentForm.js.map